/**
 * The app tooltip — one lazily-created singleton popover, delegated document listeners, no
 * per-anchor JS (interactive-charts-and-tooltips design, 2026-07-11). An element opts in by
 * carrying `data-tooltip="<already-translated label>"` (+ optional `data-tooltip-detail`); this
 * module renders it into the shared `<div popover="manual" role="tooltip" id="app-tooltip">` and
 * positions it near the anchor. Leaf module — imports nothing — so any detached-DOM builder
 * (chart marks, server-rendered fragments) can carry the attribute pair with no import of its own;
 * only page entries call `initTooltips()`.
 *
 * Mechanism: the Popover API, not a positioned div — a popover paints in the top layer, above any
 * open `<dialog>` (the progression overlay's chart, the hands/breakdown overlays' bid tags) with no
 * stacking bookkeeping (CODING § Default to defaults). On each show the singleton is reparented to
 * `anchor.closest("dialog[open]") ?? document.body`: a modal `showModal()` makes everything outside
 * the dialog inert, so a body-parented tooltip inside an open overlay would be inert to assistive
 * tech and its `aria-describedby` would point at an unreachable node.
 *
 * State machine: `idle → pending(anchor, timer) → visible(anchor)`. Every listener below is a
 * transition on it — see each handler's own comment for its transition and why.
 */

const TOOLTIP_ID = "app-tooltip";

// play-test: long enough to not flicker on a fast pass-over, far snappier than native ~500ms+.
export const SHOW_DELAY_MS = 200;

// why: the pill's standoff from the anchor / the viewport edge — small, deliberately not a token
// (a chrome-scale geometry constant, not a shared spacing unit).
const GAP_PX = 8;
const VIEWPORT_MARGIN_PX = 8;

let installed = false;
let phase = "idle"; // "idle" | "pending" | "visible"
let anchor = null;
let showTimer = null;
// The pointer's last reported position — the hover-intent discriminator in onPointerOver. Owned by
// onPointerMove alone: a boundary crossing only reports where the pointer ENTERED an element, so it
// stops tracking the moment the pointer drifts on inside it, and that stale entry coordinate is
// exactly what would make a parked cursor look like it moved.
let lastPointerX = null;
let lastPointerY = null;
let tooltipEl = null;
let labelEl = null;
let detailEl = null;

function ensureEl() {
  if (tooltipEl) return tooltipEl;
  tooltipEl = document.createElement("div");
  tooltipEl.id = TOOLTIP_ID;
  tooltipEl.className = "app-tooltip";
  tooltipEl.setAttribute("popover", "manual");
  tooltipEl.setAttribute("role", "tooltip");
  labelEl = document.createElement("div");
  labelEl.className = "app-tooltip-label";
  detailEl = document.createElement("div");
  detailEl.className = "app-tooltip-detail";
  detailEl.hidden = true; // persistent child, conditional VISIBILITY — not conditional creation
  tooltipEl.append(labelEl, detailEl);
  return tooltipEl;
}

function isInteractiveAnchor(target) {
  return target.closest("button, a, [role=button]") != null;
}

function clearPendingTimer() {
  if (showTimer == null) return;
  clearTimeout(showTimer);
  showTimer = null;
}

function renderContent(target) {
  labelEl.textContent = target.dataset.tooltip ?? "";
  const detail = target.dataset.tooltipDetail;
  detailEl.hidden = !detail;
  detailEl.textContent = detail ?? "";
}

// Reparents into the anchor's own modal (see file header) or back under body. Runs on every show,
// including a hot-track swap — the new anchor may sit in a different dialog than the old one.
function reparent(target) {
  const host = target.closest("dialog[open]") ?? document.body;
  if (tooltipEl.parentNode !== host) host.appendChild(tooltipEl);
}

// Reads (both rects) batched before the one write (left/top) — no read-write interleave
// (docs/PERFORMANCE.md). Runs after showPopover()/content-swap, so tipRect reflects the content
// that's about to be shown. Centered above with an 8px gap; flips below when that would clip the
// viewport top; clamps horizontally to the viewport with an 8px margin. No tracking loop — a shown
// tooltip is static (scroll hides it instead, below).
function position(target) {
  const anchorRect = target.getBoundingClientRect();
  const tipRect = tooltipEl.getBoundingClientRect();
  let top = anchorRect.top - tipRect.height - GAP_PX;
  if (top < 0) top = anchorRect.bottom + GAP_PX;
  const centeredLeft =
    anchorRect.left + anchorRect.width / 2 - tipRect.width / 2;
  const left = Math.min(
    Math.max(centeredLeft, VIEWPORT_MARGIN_PX),
    window.innerWidth - tipRect.width - VIEWPORT_MARGIN_PX,
  );
  tooltipEl.style.left = `${left}px`;
  tooltipEl.style.top = `${top}px`;
}

// The one path to "visible" — a fresh show (idle/pending → visible) or a hot-track swap
// (visible → visible on a different anchor, content + position update with no re-delay). Only a
// fresh show calls showPopover() (calling it while already open throws).
function showTooltip(target) {
  clearPendingTimer();
  const wasVisible = phase === "visible";
  const previousAnchor = wasVisible ? anchor : null;
  phase = "visible";
  anchor = target;
  ensureEl();
  renderContent(target);
  reparent(target);
  if (!wasVisible) tooltipEl.showPopover();
  position(target);
  if (previousAnchor != null && previousAnchor !== target) {
    previousAnchor.removeAttribute("aria-describedby");
  }
  target.setAttribute("aria-describedby", TOOLTIP_ID);
}

function armPending(target) {
  clearPendingTimer();
  phase = "pending";
  anchor = target;
  showTimer = setTimeout(() => showTooltip(target), SHOW_DELAY_MS);
}

function hideTooltip() {
  clearPendingTimer();
  if (phase === "visible") {
    tooltipEl.hidePopover();
    anchor.removeAttribute("aria-describedby");
  }
  phase = "idle";
  anchor = null;
}

// Mouse/pen only — touch's show path is pointerup below (the burst pointerover→down→up→out a tap
// fires would otherwise arm-and-immediately-race a delayed show). Entering the current anchor's own
// descendants re-fires pointerover with the same closest() target — a no-op via the `=== anchor`
// guard, whether idle→pending or already pending/visible.
function onPointerOver(e) {
  if (e.pointerType === "touch") return;
  const target = e.target.closest?.("[data-tooltip]");
  if (target === anchor) return; // includes the same anchor re-rendering under a still cursor
  // A crossing AT the pointer's own last reported position means the cursor never moved to get
  // here — the UI moved under it (an overlay opening over the button that opened it, a card
  // animating past). That is a boundary crossing but not a hover: the user pointed at nothing, so
  // nothing gets a tooltip, and whatever was armed for the context that just went away is
  // cancelled. Without this, an overlay opening under a parked cursor raises a tooltip nobody
  // asked for — which then swallows the user's first Escape (onKeyDown below) instead of letting
  // it close the overlay. A real hover always crosses in from somewhere else, so its coordinates
  // differ from the last pointermove's and it arms normally.
  if (e.clientX === lastPointerX && e.clientY === lastPointerY) {
    hideTooltip();
    return;
  }
  if (target == null) return;
  if (phase === "visible") {
    showTooltip(target); // hot-tracking: different anchor while one is already shown
  } else {
    armPending(target);
  }
}

// Tracks where the pointer actually is, so onPointerOver can tell "the cursor arrived here" from
// "this arrived under the cursor". Reads only the coordinates the event already carries — no DOM
// reads or writes, so it adds nothing to the input path's cost (docs/PERFORMANCE.md).
function onPointerMove(e) {
  if (e.pointerType === "touch") return;
  lastPointerX = e.clientX;
  lastPointerY = e.clientY;
}

// Hide/cancel only when the pointer actually left the anchor: still inside the anchor's own
// subtree, or moving straight into another tooltip anchor (whose own pointerover retargets
// instead, above) — either way this is not a real "leave". Touch pointerout is ignored entirely:
// on tap devices the whole pointerover→down→up→out burst fires within one contact, and reacting to
// this one would flash-hide a tooltip the pointerup handler just showed.
function onPointerOut(e) {
  if (e.pointerType === "touch") return;
  if (phase === "idle") return;
  const leftAnchor = e.target.closest?.("[data-tooltip]");
  if (leftAnchor !== anchor) return;
  const related = e.relatedTarget;
  if (related != null && anchor.contains(related)) return;
  if (related?.closest?.("[data-tooltip]") != null) return;
  hideTooltip();
}

// The tap path: show AFTER the contact (so it survives it), never on an interactive anchor — a tap
// on a button must be the action, not a tooltip flash first.
function onPointerUp(e) {
  if (e.pointerType !== "touch") return;
  const target = e.target.closest?.("[data-tooltip]");
  if (target == null || isInteractiveAnchor(target)) return;
  showTooltip(target);
}

function onPointerCancel(e) {
  if (e.pointerType !== "touch") return;
  hideTooltip();
}

// Serves natively focusable anchors (buttons, prompt tiles, links) — chart marks and stat icons
// deliberately gain no tabindex (see the design's interactive-anchor rule), so they never reach
// this path.
function onFocusIn(e) {
  const target = e.target.closest?.("[data-tooltip]");
  if (target == null) return;
  showTooltip(target);
}

function onFocusOut(e) {
  const target = e.target.closest?.("[data-tooltip]");
  if (target == null || target !== anchor) return;
  hideTooltip();
}

// Touch dismissal + the natural click-through hide — a pointerdown anywhere outside the current
// anchor closes it (a press ON the anchor itself, e.g. the touch tap's own down, is not a dismissal).
function onPointerDownOutside(e) {
  if (phase === "idle") return;
  if (anchor.contains(e.target)) return;
  hideTooltip();
}

// A tooltip is static once SHOWN — nothing repositions it, so a scroll would leave it floating at
// a stale spot; hiding rather than repositioning is cheap and honest. Capture so any scrollable
// ancestor's scroll is seen.
//
// Pending is deliberately exempt. Nothing is painted yet, and showTooltip's position() re-reads
// the anchor's rect at show time, so a pending tooltip has no stale position to correct — while
// cancelling one is unrecoverable under a stationary pointer: hideTooltip clears the timer and
// drops to idle, and only a fresh boundary crossing re-arms. Since this listener is document-wide
// and capture-phase, ANY scroll anywhere in the page reached it, so a late layout settle landing
// inside the 200ms window silently ate a hover the user had earned, until they jiggled the mouse.
// The genuine "the anchor scrolled out from under the pointer" case is not this handler's:
// Chrome re-evaluates hover after a scroll and fires the boundary events (the same reason `:hover`
// updates on scroll), so onPointerOut cancels it — deferred to after the scroll, so a show can
// briefly precede the cancel. That ordering is reasoned from observable hover behavior, not
// measured here; it is the tolerable direction either way, since position() reads the anchor's
// rect fresh at show time and the later pointerout still hides.
function onScrollCapture() {
  if (phase === "visible") hideTooltip();
}

// The tooltip is the topmost transient: one Escape dismisses it and consumes the event, so the
// NEXT Escape reaches an enclosing dialog — mirroring the platform's close-request stacking without
// adopting it. Idle/pending are untouched (nothing to consume for).
function onKeyDown(e) {
  if (e.key !== "Escape" || phase !== "visible") return;
  e.preventDefault();
  e.stopPropagation();
  hideTooltip();
}

/** Installs the delegated document listeners once (idempotent — a repeat call is a no-op). Call
 *  from every page entry that mounts tooltip-bearing DOM. */
export function initTooltips() {
  if (installed) return;
  installed = true;
  document.addEventListener("pointerover", onPointerOver);
  document.addEventListener("pointermove", onPointerMove, { passive: true });
  document.addEventListener("pointerout", onPointerOut);
  document.addEventListener("pointerup", onPointerUp);
  document.addEventListener("pointercancel", onPointerCancel);
  document.addEventListener("focusin", onFocusIn);
  document.addEventListener("focusout", onFocusOut);
  document.addEventListener("pointerdown", onPointerDownOutside);
  document.addEventListener("scroll", onScrollCapture, {
    capture: true,
    passive: true,
  });
  document.addEventListener("keydown", onKeyDown);
}
